import React, { ChangeEvent, useState, useEffect } from 'react';
import {
    Box, Button, Typography, Divider, Container, TextField, MenuItem,
    FormControlLabel, Radio, RadioGroup, FormControl, FormLabel
} from '@mui/material';
import { CloudUpload, Send, Delete, Close } from '@mui/icons-material';

interface MediaUploaderProps {
    preview: string | null;
    fileType: string | null;
    onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClearMedia: () => void;
    onSubmitMedia?: (payload: { mediaUrl: string | null; lifetimeMinutes: number | null; isDefault: boolean }) => void;
    currentMedia?: { media_id: string; redirect_url?: string; lifetime_minutes?: number; use_as_default?: boolean } | null;

    // ADD is_default HERE:
    onUpdateMedia?: (mediaId: string, payload: {
        redirect_url?: string;
        lifetime_minutes?: number;
        is_default?: boolean
    }) => void;

    onDeleteMedia?: (mediaId: string) => void;
    poppins: { fontFamily: string };
    isUploadingMedia: boolean;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
                                                         preview, fileType, onImageChange, onClearMedia, onSubmitMedia,
                                                         isUploadingMedia, poppins, currentMedia, onUpdateMedia, onDeleteMedia
                                                     }) => {

    const isEditMode = !!currentMedia;
    const [externalUrl, setExternalUrl] = useState<string>(currentMedia?.redirect_url || '');
    const [lifetime, setLifetime] = useState<number | string>(currentMedia?.lifetime_minutes || 'infinite');
    const [isDefault, setIsDefault] = useState<boolean>(currentMedia?.use_as_default || false);

    useEffect(() => {
        if (currentMedia) {
            setExternalUrl(currentMedia.redirect_url || '');
            setLifetime(currentMedia.lifetime_minutes || 'infinite');
            setIsDefault(currentMedia.use_as_default || false);
        } else {
            setExternalUrl('');
            setLifetime('infinite');
            setIsDefault(false);
        }
    }, [currentMedia]);

    const isVideo = fileType?.startsWith('video/') || externalUrl.match(/\.(mp4|webm|mov)($|\?)/i);

    const handleAction = () => {
        const minutes = lifetime === 'infinite' ? null : Number(lifetime);

        if (isEditMode && onUpdateMedia) {
            // ADD is_default: isDefault TO THIS PAYLOAD
            onUpdateMedia(currentMedia!.media_id, {
                redirect_url: externalUrl,
                lifetime_minutes: minutes as number,
                is_default: isDefault // <--- ADD THIS
            });
        } else if (onSubmitMedia) {
            onSubmitMedia({ mediaUrl: externalUrl.trim() || null, lifetimeMinutes: minutes, isDefault });
        }
    };

    return (
        <Container maxWidth="lg" sx={{ borderRadius: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', mb: 10, pt: 5, pb: 20, bgcolor: 'white' }}>
            <Box sx={{ border: '2px dashed #E0E5F2', borderRadius: '20px', p: 3, bgcolor: '#F4F7FE', minHeight: 280, display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: '#FFF7ED', borderRadius: '12px' }}><Send sx={{ color: 'orange' }} /></Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B2559', ...poppins }}>
                            {isEditMode ? "Update Advert" : "Advert Media"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {isEditMode && (
                            <Button startIcon={<Close />} color="inherit" onClick={onClearMedia} sx={{ textTransform: 'none' }}>Cancel</Button>
                        )}
                        {isEditMode && onDeleteMedia && (
                            <Button startIcon={<Delete />} color="error" onClick={() => onDeleteMedia(currentMedia!.media_id)}>Delete</Button>
                        )}
                    </Box>
                </Box>

                {/* Preview/Upload Section */}
                {!preview ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexGrow: 1,
                            py: 4
                        }}
                    >
                        <Button
                            variant="text"
                            component="label"
                            startIcon={<CloudUpload />}
                            sx={{
                                ...poppins,
                                flexDirection: 'column',
                                color: '#707EAE',
                                textTransform: 'none'
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                            >
                                Upload Media Asset
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{ color: '#A3AED0' }}
                            >
                                (Images, GIFs, or Videos)
                            </Typography>

                            <input
                                type="file"
                                hidden
                                accept="image/*,video/*"
                                onChange={onImageChange}
                            />
                        </Button>
                    </Box>
                ) : (
                    <Box
                        textAlign="center"
                        sx={{
                            width: '100%',
                            mb: 2
                        }}
                    >
                        {preview &&
                            (isVideo ? (
                                <video
                                    src={preview}
                                    controls
                                    style={{
                                        width: '100%',
                                        maxHeight: 350,
                                        borderRadius: '16px',
                                        backgroundColor: '#000'
                                    }}
                                />
                            ) : (
                                <img
                                    src={preview}
                                    alt="preview"
                                    style={{
                                        width: '100%',
                                        maxHeight: 220,
                                        borderRadius: '16px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ))}

                        {!isEditMode && (
                            <Button
                                size="small"
                                color="error"
                                onClick={onClearMedia}
                                sx={{
                                    mt: 1,
                                    ...poppins
                                }}
                            >
                                Remove Media
                            </Button>
                        )}
                    </Box>
                )}

                <TextField fullWidth label="Redirect Website URL" variant="outlined" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} sx={{ mt: 3, bgcolor: 'white', borderRadius: '12px' }} />

                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                {/* Use as Default Radio */}
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <FormLabel component="legend" sx={{ ...poppins, fontSize: '0.8rem' }}>Set as Default Fallback Media</FormLabel>
                    <RadioGroup row value={isDefault ? "yes" : "no"} onChange={(e) => setIsDefault(e.target.value === "yes")}>
                        <FormControlLabel value="yes" control={<Radio sx={{ color: 'orange', '&.Mui-checked': { color: 'orange' } }} />} label="Yes, use as default" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                </FormControl>

                <TextField select fullWidth label="Online Retention Lifetime Window" value={lifetime} onChange={(e) => setLifetime(e.target.value)} sx={{ mb: 3 }}>
                    <MenuItem value="infinite">Keep Online Indefinitely</MenuItem>
                    <MenuItem value={15}>15 Minutes</MenuItem>
                    <MenuItem value={60}>1 Hour</MenuItem>
                    <MenuItem value={1440}>24 Hours</MenuItem>
                    <MenuItem value={10080}>7 Days</MenuItem>
                </TextField>

                <Button variant="contained" fullWidth onClick={handleAction} sx={{ bgcolor: 'orange', py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#E28700' }, ...poppins }}>
                    {isUploadingMedia ? "Processing..." : (isEditMode ? "Update Advert" : "Upload Advert")}
                </Button>
            </Box>
        </Container>
    );
};

export default MediaUploader;